import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    inject,
    Input,
    OnInit
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Permission, Role } from 'app/enums';
import { SelectRolesForm } from './select-roles.model';
import { PermissionService } from '@services/index';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-select-roles',
    templateUrl: './select-roles.component.html',
    styleUrl: './select-roles.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectRolesComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectRolesComponent implements ControlValueAccessor, OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly permissionService = inject(PermissionService);
    private readonly translateService = inject(TranslateService);

    @Input() public disabled: boolean;

    public selectRolesForm: FormGroup<SelectRolesForm>;

    public readonly superAdminRole = Role.SuperAdmin;
    public readonly alternativeRole = Role.AlternativeRole;
    public readonly canManageRestrictedRoles = this.permissionService.isUnrestricted();
    public readonly canAssignRestrictedRoles = this.permissionService.hasAnyPermission([Permission.WriteFirmware]);

    public get f() {
        return this.selectRolesForm.controls;
    }

    public ngOnInit() {
        const user = this.value.includes(Role.User);
        const superAdmin = this.value.includes(Role.SuperAdmin);
        const fb = new FormBuilder();
        this.selectRolesForm = fb.nonNullable.group({
            [Role.Admin]: this.value.includes(Role.Admin),
            [Role.User]: user,
            [Role.SuperAdmin]: superAdmin,
            [Role.AlternativeRole]: this.value.includes(Role.AlternativeRole)
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched = () => {};
    public onChange = (value: Role[]): Role[] => value;
    public roles = [Role.Admin, Role.User, Role.SuperAdmin, Role.AlternativeRole];
    public value: Role[] = [];

    public getRoleText(role: Role): string {
        const roleName = this.translateService.instant('ROLES.' + role) as string;
        return roleName;
    }
    public writeValue(value: Role[]) {
        if (!this.disabled) {
            this.value = value;
            this.roles.forEach((role) => {
                const checked = value.includes(role);
                if (checked) this.f[role].enable();
                this.f[role].setValue(checked);
            });
            this.changeDetectorRef.markForCheck();
        }
    }

    public registerOnChange(fn: (value: Role[]) => Role[]) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    public setDisabledState?(disabled: boolean) {
        this.disabled = disabled;
        if (disabled) this.selectRolesForm.disable();
        this.changeDetectorRef.markForCheck();
    }

    public roleChange(role: Role, checked: boolean) {
        const isRestrictedRole = role === Role.SuperAdmin || role === Role.AlternativeRole;
        if (isRestrictedRole && !this.canAssignRestrictedRoles) {
            this.f[role].setValue(this.value.includes(role));
            this.changeDetectorRef.markForCheck();
            return;
        }

        // if (checked) {
        //     if (role === Role.Reader) {
        //         this.f[Role.User].setValue(false);
        //         this.f[Role.Admin].setValue(false);
        //         this.selectRolesForm.controls[Role.Admin].disable();
        //     }
        //     if (role === Role.User) {
        //         this.f[Role.Reader].setValue(false);
        //         this.selectRolesForm.controls[Role.Admin].enable();
        //     }
        // }
        const roles = this.roles.filter((r) => (r === role ? checked : this.value.includes(r)));
        this.value = roles;
        this.onChange(roles);
    }
}
